// 🔧 scoring_factor_helpers.js
// ✅ Purpose: Centralized loader/helper file for Points / Scorecard scoring setup
// ✅ Features:
// - Loads event-scoped scoring factors from Firestore
// - Loads key highlights nested under each scoring factor
// - Sorts scoring factors by sortOrder, then name
// - Sorts key highlights by sortOrder, then name
// - Returns one hydrated scoring setup object for vote.js, ballot.js, leaderboard.js, audit.js
// - Keeps scoring setup reads out of individual pages
// - Supports Points / Scorecard voting setup without reusing categories
// - Fully block-commented for clarity and future devs

import {
  collection,
  getDocs,
  orderBy,
  query,
} from "firebase/firestore";

import { db } from "../firebase";
import {
  getActiveScoringFactors,
  validateScoringFactorsTotal,
} from "./points_helpers";

/**
 * ✅ Loads all scoring factors and their nested key highlights for an event.
 *
 * Firestore structure:
 *
 * events/{eventId}/scoringFactors/{factorId}
 * events/{eventId}/scoringFactors/{factorId}/highlights/{highlightId}
 *
 * Returned shape:
 *
 * [
 *   {
 *     id: "body",
 *     name: "Body",
 *     maxPoints: 15,
 *     sortOrder: 1,
 *     active: true,
 *     highlights: [
 *       {
 *         id: "contour",
 *         name: "Contour",
 *         sortOrder: 1,
 *         active: true
 *       }
 *     ]
 *   }
 * ]
 */
export async function getScoringFactors(eventId) {
  if (!eventId) {
    console.warn("getScoringFactors called without an eventId.");
    return [];
  }

  try {
    console.log("Loading scoring factors for event:", eventId);

    const factorsRef = collection(db, "events", eventId, "scoringFactors");
    const factorsQuery = query(factorsRef, orderBy("sortOrder", "asc"));
    const factorsSnap = await getDocs(factorsQuery);

    const rawFactors = factorsSnap.docs.map((factorDoc) => ({
      id: factorDoc.id,
      ...factorDoc.data(),
    }));

    const hydratedFactors = [];

    for (const factor of rawFactors) {
      const highlightsRef = collection(
        db,
        "events",
        eventId,
        "scoringFactors",
        factor.id,
        "highlights"
      );

      const highlightsQuery = query(highlightsRef, orderBy("sortOrder", "asc"));
      const highlightsSnap = await getDocs(highlightsQuery);

      const highlights = highlightsSnap.docs
        .map((highlightDoc) => ({
          id: highlightDoc.id,
          ...highlightDoc.data(),
        }))
        .filter((highlight) => highlight?.active !== false)
        .sort((a, b) => {
          const sortA = Number(a?.sortOrder || 0);
          const sortB = Number(b?.sortOrder || 0);

          if (sortA !== sortB) {
            return sortA - sortB;
          }

          return String(a?.name || "").localeCompare(String(b?.name || ""));
        });

      hydratedFactors.push({
        ...factor,
        highlights,
      });
    }

    return getActiveScoringFactors(hydratedFactors);
  } catch (error) {
    console.error("Error loading scoring factors:", error);
    return [];
  }
}

/**
 * ✅ Builds a blank scorecard for vote.js.
 *
 * Important:
 * - This does NOT store checked state in the scoring setup.
 * - This creates ballot-level checked state for the voter’s saved vote.
 *
 * checkedHighlights shape:
 *
 * [
 *   {
 *     highlightId: "contour",
 *     highlightName: "Contour",
 *     checked: true
 *   }
 * ]
 */
export function buildBlankPointsScorecard(scoringFactors = []) {
  return getActiveScoringFactors(scoringFactors).map((factor) => ({
    factorId: factor.id,
    factorName: factor.name || "",
    pointsAwarded: "",
    maxPoints: Number(factor.maxPoints || 0),

    checkedHighlights: (factor.highlights || []).map((highlight) => ({
      highlightId: highlight.id,
      highlightName: highlight.name || "",
      checked: false,

      // ✅ Scorecard Mode support
      // In Simple Mode, this can remain blank/unused.
      // In Scorecard Mode, each highlight acts like a scored criterion.
      pointsAwarded: "",
      maxPoints: Number(highlight.maxPoints || 0),
    })),
  }));
}

/**
 * ✅ Converts a scorecard into the Firestore save shape.
 *
 * This keeps only the voter’s scoring choices:
 * - pointsAwarded
 * - maxPoints
 * - checkedHighlights
 *
 * The checked flag is preserved here.
 */
export function preparePointsScoresForSave(scorecard = []) {
  return scorecard.map((score) => {
    const checkedHighlights = (score.checkedHighlights || []).map((highlight) => ({
      highlightId: highlight.highlightId,
      highlightName: highlight.highlightName,
      checked: highlight.checked === true,

      // ✅ Scorecard Mode support
      // Preserves scored highlight/criterion points when present.
      pointsAwarded: Number(highlight.pointsAwarded || 0),
      maxPoints: Number(highlight.maxPoints || 0),
    }));

    // ✅ If highlights have maxPoints, treat this as Scorecard Mode
    // and roll highlight scores up into the factor score.
    const hasScoredHighlights = checkedHighlights.some(
      (highlight) => Number(highlight.maxPoints || 0) > 0
    );

    const rolledUpPoints = checkedHighlights.reduce(
      (sum, highlight) => sum + Number(highlight.pointsAwarded || 0),
      0
    );

    const rolledUpMaxPoints = checkedHighlights.reduce(
      (sum, highlight) => sum + Number(highlight.maxPoints || 0),
      0
    );

    return {
      factorId: score.factorId,
      factorName: score.factorName,

      pointsAwarded: hasScoredHighlights
        ? rolledUpPoints
        : Number(score.pointsAwarded || 0),

      maxPoints: hasScoredHighlights
        ? rolledUpMaxPoints
        : Number(score.maxPoints || 0),

      checkedHighlights,
    };
  });
} 

/**
 * ✅ Validates whether the event scoring setup is ready for voting.
 *
 * Default maxBallotPoints is 100.
 */
export function validateScoringSetupReady(
  scoringFactors = [],
  maxBallotPoints = 100
) {
  const activeFactors = getActiveScoringFactors(scoringFactors);
  const totalValidation = validateScoringFactorsTotal(
    activeFactors,
    maxBallotPoints
  );

  if (!activeFactors.length) {
    return {
      isReady: false,
      message: "No scoring factors have been configured yet.",
    };
  }

  if (!totalValidation.isValid) {
    return {
      isReady: false,
      message: totalValidation.message,
    };
  }

  return {
    isReady: true,
    message: "Scoring setup is ready.",
  };
}

/**
 * ✅ Rebuilds a scorecard from current scoring factors and an existing saved vote.
 *
 * Why this matters:
 * - The event setup is the source of truth for current factors/highlights
 * - The saved vote is the source of truth for the judge's previous points/checks
 * - This safely merges both together so old saved values refill the form
 */
export function buildPointsScorecardFromExistingVote(
  scoringFactors = [],
  existingScores = []
) {
  const blankScorecard = buildBlankPointsScorecard(scoringFactors);

  return blankScorecard.map((blankScore) => {
    const savedScore = existingScores.find(
      (score) => score.factorId === blankScore.factorId
    );

    if (!savedScore) {
      return blankScore;
    }

    return {
      ...blankScore,

      pointsAwarded:
        savedScore.pointsAwarded === 0 || savedScore.pointsAwarded
          ? savedScore.pointsAwarded
          : "",

      checkedHighlights: blankScore.checkedHighlights.map((blankHighlight) => {
        const savedHighlight = (savedScore.checkedHighlights || []).find(
          (highlight) =>
            highlight.highlightId === blankHighlight.highlightId
        );

        return {
          ...blankHighlight,
          checked: savedHighlight?.checked === true,

          // ✅ Scorecard Mode support
          // Rehydrates scored highlight/criterion values from saved vote.
          pointsAwarded:
            savedHighlight?.pointsAwarded === 0 || savedHighlight?.pointsAwarded
              ? savedHighlight.pointsAwarded
              : "",

          maxPoints:
            savedHighlight?.maxPoints === 0 || savedHighlight?.maxPoints
              ? Number(savedHighlight.maxPoints)
              : Number(blankHighlight.maxPoints || 0),
        };
      }),
    };
  });
}